/**
 * PAINT (docs/bim-plan/21 §4.5 — K5; Matt 2026-09-27: "show the front and back side
 * of a face as two different colors, like in SketchUp … a material paint bucket that
 * behaves just like SketchUp … right click on the face … modify its scale and
 * position just like in SketchUp").
 *
 * Every face has two SIDES. A side is painted with a material id, or unpainted —
 * and an unpainted side renders the body's own default (a group/component's
 * material, else SketchUp's two-tone white front / blue-gray back, the pseudo
 * materials below) so orientation always stays legible.
 *
 * TEXTURE POSITION. A side's texture coordinates are world FEET in that side's own
 * face frame (`bodyTexFrame` — u runs horizontally, v runs up the face, as you look
 * at that side), passed through an optional per-side affine `BodyUvXf` (SketchUp's
 * Texture > Position result). The renderer divides by the material's tile size live,
 * so a tile-size edit never regenerates geometry — the Round A convention.
 */
import type { BodyTopology, BodyFace, BodyFid, BodyPt3, BodyLoop } from './types'
import { facesUsingEdge } from './topo'

/** SketchUp's default two-tone look — pseudo material ids, never stored records */
export const BODY_FRONT_MAT = '__bodyFront'
export const BODY_BACK_MAT = '__bodyBack'
export const BODY_FRONT_COLOR = '#ffffff'
export const BODY_BACK_COLOR = '#a4b4c4'

export type BodySide = 'front' | 'back'

/**
 * Texture affine, texture feet from face-frame feet: [a, b, c, d, e, f] means
 *   Tu = a·qu + b·qv + c,   Tv = d·qu + e·qv + f
 * Absent = identity (the texture's origin at the frame origin, one texture foot per foot).
 */
export type BodyUvXf = [number, number, number, number, number, number]
export const UV_IDENTITY: BodyUvXf = [1, 0, 0, 0, 1, 0]

type V3 = { x: number; y: number; z: number }
const norm = (a: V3): V3 => {
  const l = Math.hypot(a.x, a.y, a.z)
  return l > 0 ? { x: a.x / l, y: a.y / l, z: a.z / l } : { x: 0, y: 0, z: 1 }
}
const cross = (a: V3, b: V3): V3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x })
const dot = (a: V3, b: V3) => a.x * b.x + a.y * b.y + a.z * b.z

/** the paint fields of a face, spread-ready — every op that re-creates a face
 *  from another carries these so paint and texture position survive the edit */
export function facePaint(f: BodyFace | undefined | null): Partial<BodyFace> {
  const o: Partial<BodyFace> = {}
  if (!f) return o
  if (f.frontMat !== undefined) o.frontMat = f.frontMat
  if (f.backMat !== undefined) o.backMat = f.backMat
  if (f.frontUv !== undefined) o.frontUv = [...f.frontUv] as BodyUvXf
  if (f.backUv !== undefined) o.backUv = [...f.backUv] as BodyUvXf
  return o
}

/** the side of a face whose normal is `n` that a ray travelling along `d` sees */
export function sideSeen(n: V3, d: V3 | [number, number, number]): BodySide {
  const dv = Array.isArray(d) ? { x: d[0], y: d[1], z: d[2] } : d
  return dot(n, dv) < 0 ? 'front' : 'back'
}

/**
 * The face frame a side's texture lives in: u horizontal, v up the face, both as
 * seen FROM that side (so a back-side texture reads the right way round from behind,
 * not mirrored). A horizontal face uses world x/y (mirrored correctly underneath).
 */
export function bodyTexFrame(n0: V3, side: BodySide): { u: V3; v: V3; n: V3 } {
  const n1 = norm(n0)
  const n = side === 'back' ? { x: -n1.x, y: -n1.y, z: -n1.z } : n1
  let u: V3
  if (Math.abs(n.z) > 0.999) u = { x: 1, y: 0, z: 0 }
  else u = norm(cross({ x: 0, y: 0, z: 1 }, n))
  const v = cross(n, u)
  return { u, v, n }
}

/** texture feet of point p on a side (frame + the side's affine) */
export function faceUvAt(p: V3, frame: { u: V3; v: V3 }, xf?: BodyUvXf | null): [number, number] {
  const qu = dot(p, frame.u), qv = dot(p, frame.v)
  if (!xf) return [qu, qv]
  return [xf[0] * qu + xf[1] * qv + xf[2], xf[3] * qu + xf[4] * qv + xf[5]]
}

/** a side's stored paint (undefined/null = unpainted) */
export function sideMat(f: BodyFace, side: BodySide): string | null {
  return (side === 'front' ? f.frontMat : f.backMat) ?? null
}

/**
 * Faces the Paint Bucket paints from a click on (fid, side) — SketchUp's modes:
 *   'one'       plain click: that face
 *   'connected' (Ctrl) that face plus every face connected to it through shared
 *               edges whose same side carries the same material
 *   'matching'  (Ctrl+Shift, within this body) every face in the body whose same
 *               side carries the same material, connected or not
 * (Shift — every matching face in the whole editing context — spans bodies, so the
 * page loops `matching` over each body.)
 */
export function paintTargets(topo: BodyTopology, fid: BodyFid, side: BodySide, mode: 'one' | 'connected' | 'matching'): BodyFid[] {
  const f0 = topo.faces[fid]
  if (!f0) return []
  if (mode === 'one') return [fid]
  const want = sideMat(f0, side)
  if (mode === 'matching') {
    return Object.entries(topo.faces).filter(([, f]) => sideMat(f, side) === want).map(([id]) => id)
  }
  const seen = new Set<BodyFid>([fid])
  const stack = [fid]
  while (stack.length) {
    const cur = stack.pop()!
    for (const loop of topo.faces[cur].loops) {
      for (const eid of loop.edges) {
        for (const nb of facesUsingEdge(topo, eid)) {
          if (seen.has(nb) || !topo.faces[nb]) continue
          if (sideMat(topo.faces[nb], side) !== want) continue
          seen.add(nb)
          stack.push(nb)
        }
      }
    }
  }
  return [...seen]
}

function reverseLoop(l: BodyLoop): BodyLoop {
  return { edges: [...l.edges].reverse(), dirs: [...l.dirs].reverse().map((d) => !d) }
}

/** SketchUp's Reverse Faces: the face turns around — normal, loop winding, and its
 *  two sides' paint and texture positions all swap */
export function reverseFaceInPlace(topo: BodyTopology, fid: BodyFid): boolean {
  const f = topo.faces[fid]
  if (!f) return false
  const next: BodyFace = {
    plane: { n: { x: -f.plane.n.x, y: -f.plane.n.y, z: -f.plane.n.z }, d: -f.plane.d },
    loops: f.loops.map(reverseLoop),
  }
  if (f.backMat !== undefined) next.frontMat = f.backMat
  if (f.frontMat !== undefined) next.backMat = f.frontMat
  if (f.backUv !== undefined) next.frontUv = f.backUv
  if (f.frontUv !== undefined) next.backUv = f.frontUv
  topo.faces[fid] = next
  return true
}

/** set one side's paint; painting clears that side's texture position (a new
 *  material starts from the default placement, as in SketchUp) */
export function paintFaceSide(topo: BodyTopology, fid: BodyFid, side: BodySide, materialId: string | null): boolean {
  const f = topo.faces[fid]
  if (!f) return false
  const matKey = side === 'front' ? 'frontMat' : 'backMat'
  const uvKey = side === 'front' ? 'frontUv' : 'backUv'
  const same = (f[matKey] ?? null) === materialId
  if (materialId) f[matKey] = materialId
  else delete f[matKey]
  if (!same) delete f[uvKey]
  return true
}

/** a point on the face's plane from face-frame feet (for pins / overlays) */
export function framePoint(frame: { u: V3; v: V3; n: V3 }, planeD: number, planeN: V3, qu: number, qv: number): BodyPt3 {
  const n1 = norm(planeN)
  // n1·p = d (the stored plane is unit-normal by construction; normalize defensively)
  const s = planeD / (Math.hypot(planeN.x, planeN.y, planeN.z) || 1)
  return {
    x: frame.u.x * qu + frame.v.x * qv + n1.x * s,
    y: frame.u.y * qu + frame.v.y * qv + n1.y * s,
    z: frame.u.z * qu + frame.v.z * qv + n1.z * s,
  }
}
